import { create } from 'zustand';
import { advanceResync } from './resync.store';

/**
 * 036 (issue 208) — what the application has OBSERVED about the server, from the outcome of real
 * requests only (research R-005). `core/pwa/connectivity.ts` stays exactly as it is: what the
 * BROWSER reports. This is a second, independent fact — what asking the server has actually shown,
 * which `navigator.onLine` cannot: a captive portal, a stalled proxy, or a server answering every
 * request with a `5xx` are all "online" by the browser's own measure.
 *
 *  | outcome of a request                                   | sets           |
 *  |----------------------------------------------------------|---------------|
 *  | any response with a status below 500                     | `reachable`   |
 *  | `fetch` rejected, or no answer within the limit (`0`)     | `unreachable` |
 *  | a `5xx`                                                   | `failing`     |
 *
 * `'unknown'` is the fourth value this store never reports as a verdict — it is what every session
 * starts in, before a single request has settled either way, and it must never be read as either
 * bad state: `'unreachable'`/`'failing'` are both claims this store has actually observed
 * something, and inventing one before that would be *the absence of a distinction read as one of
 * its branches* — a failure this repository has already paid for (014's liveness check, 021's
 * offline launch) and is not paying for a third time here.
 *
 * **`noteOutcome` is the only writer, and it is never called to PROBE.** Every call is the side
 * effect of a request something else already needed — `observing-client.ts` for ordinary
 * application traffic, `core/outbox/outbox.store.ts` for its own sends. Sending a request for the
 * sole purpose of finding this out would measure the probe rather than the practitioner's own
 * traffic (`e2e/home-scale.spec.ts` counts requests exactly; research R-005 rejects a health-check
 * endpoint for the same reason).
 */
export type ReachState = 'unknown' | 'reachable' | 'unreachable' | 'failing';

interface ReachStoreState {
  readonly state: ReachState;
  /**
   * How many times a request OTHER than one of the outbox's own sends has succeeded — the bad-row
   * rule's evidence (issue 208, "a bad row told from a bad server"). Restarts at zero on every
   * reload, deliberately: a row compares against this counter to decide whether the SERVER has
   * shown a sign of life since the row's own last strike, and that question only ever means
   * "since", never "across a reload this counter cannot remember" — `device-database.ts`'s own
   * `evidenceMark` comment has the "stale mark" half of that.
   */
  readonly evidence: number;
}

const store = create<ReachStoreState>()(() => ({ state: 'unknown', evidence: 0 }));

/** Read inside a component. Re-renders on change. */
export const useReachState = (): ReachState => store((s) => s.state);
/** Read outside React. */
export const reachState = (): ReachState => store.getState().state;
/** The bad-row rule's evidence counter (`core/outbox/outbox.store.ts`). Read outside React only —
 *  nothing draws it. */
export const evidenceCount = (): number => store.getState().evidence;

export interface NoteOutcomeOptions {
  /**
   * True for a request the OUTBOX ITSELF made to send a row. Excluded from the evidence counter —
   * research R-005's suggested mechanism is deliberately blunt about "the outbox's own" rather than
   * narrowing to "this exact row's own attempt": a DIFFERENT row's successful send is still the
   * outbox's own traffic, and letting it count would make the bad-row rule partly
   * self-referential (the queue proving itself healthy) instead of resting on independent evidence
   * that the server is generally up. The `reachable`/`unreachable`/`failing` STATE is unaffected by
   * this flag — an outbox send is exactly as real a signal of reachability as any other request.
   */
  readonly ownSend?: boolean;
  /**
   * True for a success whose body could not be read as JSON. The API answers in JSON, so such a
   * body came from something in front of it — a proxy's error page, a captive portal, a fallback
   * serving the application's own HTML. It is an answer, so the state is `reachable` by the table
   * above, but it is excluded from the evidence counter: it shows that something answered, not
   * that the API is working.
   */
  readonly notJson?: boolean;
  /**
   * True for `core/session/session.store.ts`'s own traffic — chiefly the renewal a beat retries
   * while `unconfirmed` (research R-007). A DIFFERENT flag from `ownSend` rather than a reuse of it:
   * `ownSend` means "the outbox's own send, excluded so a row cannot count its own traffic as
   * evidence of itself"; a renewal is excluded for the opposite reason — it is exactly the request
   * an outbox send DEPENDS ON (the token a send presents), so a strike waiting on independent proof
   * the server is generally up must not accept "the session that send is riding under just renewed"
   * as that proof. Same exclusion, different justification, so a reader auditing the bad-row rule
   * later does not have to guess which one applied. The `reachable`/`unreachable`/`failing` STATE
   * still moves on every call — a renewal's outcome is exactly as real a reachability signal as any
   * other request, which is the whole reason session traffic reports here at all.
   */
  readonly sessionTraffic?: boolean;
}

/**
 * The only way this module's state changes. `status` is the raw HTTP status; `0` is `http.ts`'s own
 * code for a `fetch` that rejected or a request that never answered within its limit.
 */
export function noteOutcome(status: number, options: NoteOutcomeOptions = {}): void {
  const previous = store.getState().state;

  if (status === 0) {
    store.setState({ state: 'unreachable' });
    return;
  }
  if (status >= 500) {
    store.setState({ state: 'failing' });
    return;
  }

  store.setState((s) => ({
    state: 'reachable',
    evidence: options.ownSend || options.notJson || options.sessionTraffic ? s.evidence : s.evidence + 1,
  }));

  // R-009: a screen re-reads once the server is shown reachable again, but only on the TRANSITION —
  // not on every reachable observation (`home-scale.spec.ts`'s exact request counts would double
  // otherwise), and not on the very first observation after start ('unknown' is not a bad state to
  // recover FROM — nothing was ever shown broken).
  if (previous === 'unreachable' || previous === 'failing') advanceResync();
}

// --- the beat (research R-007): ONE retry timer, owned here, that every "try again" concern shares
// ---------------------------------------------------------------------------------------------

export interface BeatSubscriber {
  /**
   * Checked at every five-second tick — the recurring poll does nothing at all unless at least one
   * subscriber currently has something worth retrying (`e2e/home-scale.spec.ts`'s exact request
   * counts depend on this costing nothing when there is nothing to do). `online` and the document
   * becoming visible fire regardless of this: they are cheap, one-off events rather than a poll, and
   * a subscriber with nothing waiting does nothing itself when asked.
   */
  readonly hasWork: () => boolean;
  /** Called when it is time to try again. */
  readonly onBeat: () => void;
}

const subscribers = new Set<BeatSubscriber>();
let started = false;
let timer: ReturnType<typeof setInterval> | null = null;

function fireAll(): void {
  for (const subscriber of subscribers) subscriber.onBeat();
}

/**
 * 036 (issue 222, SC-044, R-009 amended) — "the screen in view re-reads on coming to the front."
 * `noteOutcome`'s own advance-on-transition rule (above) cannot cover this case: the connection was
 * fine the WHOLE time, the document was simply not being looked at, and something could have
 * changed on the server while it was not (SC-044's own scenario: an action added elsewhere while the
 * application sat in the background). So `resync` also advances here, on becoming visible, whenever
 * the state is ALREADY `reachable` — never on `unknown` (nothing has been shown broken or stale yet,
 * so there is nothing to refresh) and never on `unreachable`/`failing` (there is no point re-reading
 * a server that still cannot be reached; the transition rule fires once one answers again).
 *
 * This depends on the beat having been started at least once (`ensureBeatStarted`, below) — true of
 * every real session, since `core/outbox/outbox.store.ts`'s `startOutboxWatch()` is called
 * unconditionally from `main.tsx` and is this module's own first subscriber.
 * `core/session/session.store.ts`'s `startSessionRecovery()` is a second, also called unconditionally
 * from `main.tsx` (036, issue 219, R-007) — fixed here (issue 258, B5) after this comment named the
 * outbox as the only one twice over. `e2e/` specs never dispatch `visibilitychange` at all (CLAUDE.md's
 * own brief for this slice), so this adds nothing to `home-scale.spec.ts`'s exact request counts.
 */
function onVisibilityChange(): void {
  if (document.visibilityState !== 'visible') return;
  if (store.getState().state === 'reachable') advanceResync();
  fireAll();
}

function tick(): void {
  if (document.visibilityState !== 'visible') return;
  let anyWork = false;
  for (const subscriber of subscribers) {
    if (subscriber.hasWork()) {
      anyWork = true;
      break;
    }
  }
  if (anyWork) fireAll();
}

/**
 * Named, module-level listeners (not a fresh closure per call) so that starting the beat again
 * after a test's `resetReach()` — `watching`-style flags reset, the browser's own listener
 * deduplication does the rest — never piles up a second registration the way an inline arrow
 * function would (`core/outbox/outbox.store.ts`'s own history: its `visibilitychange` listener used
 * to be exactly that kind of closure, tolerated only because `wakeOutbox()` is idempotent).
 */
function ensureBeatStarted(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', fireAll);
  document.addEventListener('visibilitychange', onVisibilityChange);
  timer = setInterval(tick, 5000);
}

/**
 * Joins the one beat (R-007): every five seconds while the document is visible and SOME subscriber
 * has work, plus at once on `online` and on the document becoming visible. `core/outbox/` was this
 * slice's first subscriber; `core/session/session.store.ts`'s `startSessionRecovery()` is a second
 * (issue 219) — a later "try again" concern joins the same beat rather than starting its own timer,
 * which is the whole point of moving it here (corrected here, issue 258 B5: this used to say
 * "only"). Returns the unsubscribe.
 */
export function subscribeToBeat(subscriber: BeatSubscriber): () => void {
  subscribers.add(subscriber);
  ensureBeatStarted();
  return () => {
    subscribers.delete(subscriber);
  };
}

/** Test seam, like every other module-level store here. Production code never calls it. Stops the
 *  timer and forgets every subscriber, so the next `subscribeToBeat` starts clean. */
export function resetReach(): void {
  store.setState({ state: 'unknown', evidence: 0 });
  subscribers.clear();
  started = false;
  if (timer) clearInterval(timer);
  timer = null;
}
